import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import { isAdmin } from '../../utils/roleManager';
import {
  getVerification,
  updateVerificationStatus,
  updateSelfieVerificationStatus,
  updateDocumentStatus,
  deleteSelfieImage,
  getAdminVideoCallBookings,
  updateVideoCallBookingStatus,
} from '../../utils/verificationDatabase';
import { calculateVerificationScore, calculateRiskScore } from '../../data/verificationModels';

const StoreVerificationPanel = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [verifications, setVerifications] = useState([]);
  const [filter, setFilter] = useState('all'); // all, pending, in_review, approved, rejected
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [videoCallBookings, setVideoCallBookings] = useState([]);
  const [activeTab, setActiveTab] = useState('verifications'); // verifications, videoCalls

  useEffect(() => {
    if (currentUser && isAdmin(currentUser)) {
      loadVerifications();
      loadVideoCallBookings();
    }
  }, [currentUser, filter]);

  // Real-time listener for verifications
  useEffect(() => {
    if (!currentUser || !isAdmin(currentUser)) return;

    let q;
    if (filter === 'all') {
      q = query(
        collection(db, 'storeVerifications'),
        orderBy('createdAt', 'desc')
      );
    } else {
      q = query(
        collection(db, 'storeVerifications'),
        where('status', '==', filter),
        orderBy('createdAt', 'desc')
      );
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const verificationsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setVerifications(verificationsData);
      setLoading(false);
    }, (error) => {
      console.error('Error listening to verifications:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser, filter]);

  const loadVerifications = async () => {
    try {
      setLoading(true);
      // Real-time listener will handle updates
    } catch (error) {
      console.error('Error loading verifications:', error);
      setLoading(false);
    }
  };

  const loadVideoCallBookings = async () => {
    try {
      const result = await getAdminVideoCallBookings();
      if (result.success) {
        setVideoCallBookings(result.bookings);
      }
    } catch (error) {
      console.error('Error loading video call bookings:', error);
    }
  };

  const handleViewDetail = async (verificationId) => {
    try {
      const result = await getVerification(verificationId);
      if (result.success) {
        setSelectedVerification(result.verification);
        setShowDetailModal(true);
        setReviewNotes('');
      }
    } catch (error) {
      console.error('Error loading verification detail:', error);
      alert('เกิดข้อผิดพลาดในการโหลดข้อมูล');
    }
  };

  const handleApproveSelfie = async (verificationId) => {
    if (!window.confirm('ยืนยันการอนุมัติ Selfie Verification?')) return;

    try {
      const result = await updateSelfieVerificationStatus(
        verificationId,
        'approved',
        currentUser.uid,
        reviewNotes || 'ผ่านการตรวจสอบ'
      );

      if (result.success) {
        alert('อนุมัติ Selfie สำเร็จ');
        setShowDetailModal(false);
        setReviewNotes('');
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error approving selfie:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  const handleRejectSelfie = async (verificationId) => {
    if (!reviewNotes.trim()) {
      alert('กรุณากรอกเหตุผลในการปฏิเสธ');
      return;
    }

    if (!window.confirm('ยืนยันการปฏิเสธ Selfie Verification?')) return;

    try {
      const result = await updateSelfieVerificationStatus(
        verificationId,
        'rejected',
        currentUser.uid,
        reviewNotes
      );

      if (result.success) {
        alert('ปฏิเสธ Selfie สำเร็จ');
        setShowDetailModal(false);
        setReviewNotes('');
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error rejecting selfie:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  const handleApproveDocument = async (verificationId, documentType) => {
    if (!window.confirm(`ยืนยันการอนุมัติเอกสาร ${documentType}?`)) return;

    try {
      const result = await updateDocumentStatus(
        verificationId,
        documentType,
        'approved',
        currentUser.uid,
        reviewNotes || 'ผ่านการตรวจสอบ'
      );

      if (result.success) {
        alert('อนุมัติเอกสารสำเร็จ');
        // Reload detail
        handleViewDetail(verificationId);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error approving document:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  const handleRejectDocument = async (verificationId, documentType) => {
    if (!reviewNotes.trim()) {
      alert('กรุณากรอกเหตุผลในการปฏิเสธ');
      return;
    }

    if (!window.confirm(`ยืนยันการปฏิเสธเอกสาร ${documentType}?`)) return;

    try {
      const result = await updateDocumentStatus(
        verificationId,
        documentType,
        'rejected',
        currentUser.uid,
        reviewNotes
      );

      if (result.success) {
        alert('ปฏิเสธเอกสารสำเร็จ');
        // Reload detail
        handleViewDetail(verificationId);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error rejecting document:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  const handleApproveVerification = async (verificationId) => {
    if (!window.confirm('ยืนยันการอนุมัติการตรวจสอบร้านค้า?')) return;

    try {
      const result = await updateVerificationStatus(
        verificationId,
        'approved',
        currentUser.uid,
        reviewNotes || 'ผ่านการตรวจสอบ'
      );

      if (result.success) {
        alert('อนุมัติการตรวจสอบสำเร็จ');
        setShowDetailModal(false);
        setReviewNotes('');
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error approving verification:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  const handleRejectVerification = async (verificationId) => {
    if (!reviewNotes.trim()) {
      alert('กรุณากรอกเหตุผลในการปฏิเสธ');
      return;
    }

    if (!window.confirm('ยืนยันการปฏิเสธการตรวจสอบร้านค้า?')) return;

    try {
      const result = await updateVerificationStatus(
        verificationId,
        'rejected',
        currentUser.uid,
        reviewNotes
      );

      if (result.success) {
        alert('ปฏิเสธการตรวจสอบสำเร็จ');
        setShowDetailModal(false);
        setReviewNotes('');
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error rejecting verification:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  const handleDeleteSelfie = async (verificationId, imageUrl) => {
    if (!window.confirm('ยืนยันการลบรูปภาพ Selfie? (ไม่สามารถกู้คืนได้)')) return;

    try {
      const result = await deleteSelfieImage(verificationId, imageUrl);
      if (result.success) {
        alert('ลบรูปภาพ Selfie สำเร็จ');
        // Reload detail
        handleViewDetail(verificationId);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error deleting selfie:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  const handleApproveVideoCall = async (bookingId) => {
    if (!window.confirm('ยืนยันการอนุมัติ Video Call Booking?')) return;

    try {
      const result = await updateVideoCallBookingStatus(
        bookingId,
        'approved',
        currentUser.uid
      );

      if (result.success) {
        alert('อนุมัติ Video Call สำเร็จ');
        loadVideoCallBookings();
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error approving video call:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  const handleRejectVideoCall = async (bookingId, reason) => {
    if (!reason.trim()) {
      alert('กรุณากรอกเหตุผลในการปฏิเสธ');
      return;
    }

    if (!window.confirm('ยืนยันการปฏิเสธ Video Call Booking?')) return;

    try {
      const result = await updateVideoCallBookingStatus(
        bookingId,
        'rejected',
        currentUser.uid,
        reason
      );

      if (result.success) {
        alert('ปฏิเสธ Video Call สำเร็จ');
        loadVideoCallBookings();
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error rejecting video call:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_review: 'bg-blue-100 text-blue-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      expired: 'bg-gray-100 text-gray-800',
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusText = (status) => {
    const texts = {
      pending: 'รอตรวจสอบ',
      in_review: 'กำลังตรวจสอบ',
      approved: 'อนุมัติแล้ว',
      rejected: 'ปฏิเสธ',
      expired: 'หมดอายุ',
    };
    return texts[status] || status;
  };

  if (!currentUser || !isAdmin(currentUser)) {
    return (
      <div className="p-6 text-center text-red-600">
        คุณไม่มีสิทธิ์เข้าถึงหน้านี้
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">
        🏪 จัดการการตรวจสอบร้านค้า
      </h1>

      {/* Tabs */}
      <div className="flex space-x-4 mb-6 border-b">
        <button
          onClick={() => setActiveTab('verifications')}
          className={`px-4 py-2 font-semibold ${
            activeTab === 'verifications'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          การตรวจสอบร้านค้า
        </button>
        <button
          onClick={() => setActiveTab('videoCalls')}
          className={`px-4 py-2 font-semibold ${
            activeTab === 'videoCalls'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          Video Call Bookings
        </button>
      </div>

      {/* Verifications Tab */}
      {activeTab === 'verifications' && (
        <>
          {/* Filters */}
          <div className="mb-4 flex space-x-2">
            {['all', 'pending', 'in_review', 'approved', 'rejected'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-lg ${
                  filter === f
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {f === 'all' ? 'ทั้งหมด' : getStatusText(f)}
              </button>
            ))}
          </div>

          {/* Verifications List */}
          {loading ? (
            <div className="text-center py-8 text-gray-500">กำลังโหลด...</div>
          ) : verifications.length === 0 ? (
            <div className="text-center py-8 text-gray-500">ไม่พบการตรวจสอบ</div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Store ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      ชื่อ-นามสกุล
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Selfie
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      เอกสาร
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      คะแนน
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Risk Score
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      สถานะ
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      วันที่ส่ง
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      จัดการ
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {verifications.map((verification) => {
                    const docs = verification.supportingDocuments || {};
                    const uploadedDocs = Object.values(docs).filter(doc => doc.uploaded).length;
                    const approvedDocs = Object.values(docs).filter(doc => doc.status === 'approved').length;

                    return (
                      <tr key={verification.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {verification.storeId?.substring(0, 8)}...
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {verification.personalInfo?.fullName || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`px-2 py-1 rounded-full ${
                            verification.selfieVerification?.status === 'approved'
                              ? 'bg-green-100 text-green-800'
                              : verification.selfieVerification?.status === 'rejected'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {verification.selfieVerification?.status === 'approved' ? '✓' :
                             verification.selfieVerification?.status === 'rejected' ? '✗' : '⏳'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {approvedDocs}/{uploadedDocs}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {verification.verificationScore || 0}/175
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`px-2 py-1 rounded-full ${
                            (verification.riskScore || 100) >= 80
                              ? 'bg-green-100 text-green-800'
                              : (verification.riskScore || 100) >= 50
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {verification.riskScore || 100}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`px-2 py-1 rounded-full ${getStatusBadge(verification.status)}`}>
                            {getStatusText(verification.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {verification.submittedAt?.toDate?.()?.toLocaleDateString('th-TH') || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button
                            onClick={() => handleViewDetail(verification.id)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            ดูรายละเอียด
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Video Call Bookings Tab */}
      {activeTab === 'videoCalls' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Store
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  วันที่
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  เวลา
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  ระยะเวลา
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  วัตถุประสงค์
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  สถานะ
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  จัดการ
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {videoCallBookings.map((booking) => (
                <tr key={booking.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {booking.storeId?.substring(0, 8)}...
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {booking.scheduledDate}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {booking.scheduledTime}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {booking.duration} นาที
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {booking.purpose}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`px-2 py-1 rounded-full ${getStatusBadge(booking.status)}`}>
                      {getStatusText(booking.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                    {booking.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleApproveVideoCall(booking.id)}
                          className="text-green-600 hover:text-green-800"
                        >
                          อนุมัติ
                        </button>
                        <button
                          onClick={() => {
                            const reason = window.prompt('เหตุผลในการปฏิเสธ:');
                            if (reason) handleRejectVideoCall(booking.id, reason);
                          }}
                          className="text-red-600 hover:text-red-800"
                        >
                          ปฏิเสธ
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedVerification && (
        <VerificationDetailModal
          verification={selectedVerification}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedVerification(null);
            setReviewNotes('');
          }}
          onApproveSelfie={handleApproveSelfie}
          onRejectSelfie={handleRejectSelfie}
          onApproveDocument={handleApproveDocument}
          onRejectDocument={handleRejectDocument}
          onApproveVerification={handleApproveVerification}
          onRejectVerification={handleRejectVerification}
          onDeleteSelfie={handleDeleteSelfie}
          reviewNotes={reviewNotes}
          setReviewNotes={setReviewNotes}
        />
      )}
    </div>
  );
};

// Detail Modal Component
const VerificationDetailModal = ({
  verification,
  onClose,
  onApproveSelfie,
  onRejectSelfie,
  onApproveDocument,
  onRejectDocument,
  onApproveVerification,
  onRejectVerification,
  onDeleteSelfie,
  reviewNotes,
  setReviewNotes,
}) => {
  const documentLabels = {
    commercialRegistration: 'ทะเบียนพาณิชย์',
    cannabisLicense: 'ใบอนุญาตขายกัญชา',
    addressProof: 'เอกสารยืนยันที่อยู่',
    taxProof: 'หลักฐานการชำระภาษี',
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">
              รายละเอียดการตรวจสอบ
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
          </div>

          {/* Basic Info */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-3">ข้อมูลพื้นฐาน</h3>
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <p><strong>ชื่อ-นามสกุล:</strong> {verification.personalInfo?.fullName || '-'}</p>
              <p><strong>เลขบัตร 4 หลัก:</strong> {verification.personalInfo?.idCardLast4 || '-'}</p>
              <p><strong>วันเกิด:</strong> {verification.personalInfo?.birthDate || '-'}</p>
              <p><strong>Store ID:</strong> {verification.storeId}</p>
              <p><strong>Owner ID:</strong> {verification.ownerId}</p>
            </div>
          </div>

          {/* Selfie Verification */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Selfie Verification</h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <span className={`px-3 py-1 rounded-full ${
                  verification.selfieVerification?.status === 'approved'
                    ? 'bg-green-100 text-green-800'
                    : verification.selfieVerification?.status === 'rejected'
                    ? 'bg-red-100 text-red-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {verification.selfieVerification?.status === 'approved' ? 'อนุมัติแล้ว' :
                   verification.selfieVerification?.status === 'rejected' ? 'ปฏิเสธ' : 'รอตรวจสอบ'}
                </span>
                {verification.selfieVerification?.imageUrl && (
                  <button
                    onClick={() => onDeleteSelfie(verification.id, verification.selfieVerification.imageUrl)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    ลบรูปภาพ
                  </button>
                )}
              </div>
              
              {verification.selfieVerification?.imageUrl && (
                <img
                  src={verification.selfieVerification.imageUrl}
                  alt="Selfie"
                  className="max-w-md rounded-lg mb-3"
                />
              )}
              
              {verification.selfieVerification?.status === 'pending' && (
                <div className="flex space-x-2 mt-3">
                  <button
                    onClick={() => onApproveSelfie(verification.id)}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                  >
                    อนุมัติ
                  </button>
                  <button
                    onClick={() => onRejectSelfie(verification.id)}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                  >
                    ปฏิเสธ
                  </button>
                </div>
              )}
              
              {verification.selfieVerification?.reviewNotes && (
                <p className="text-sm text-gray-600 mt-2">
                  <strong>หมายเหตุ:</strong> {verification.selfieVerification.reviewNotes}
                </p>
              )}
            </div>
          </div>

          {/* Supporting Documents */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-3">เอกสารประกอบ</h3>
            <div className="space-y-3">
              {Object.entries(verification.supportingDocuments || {}).map(([docType, docData]) => (
                <div key={docType} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold">{documentLabels[docType] || docType}</span>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      docData.status === 'approved'
                        ? 'bg-green-100 text-green-800'
                        : docData.status === 'rejected'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {docData.status === 'approved' ? 'อนุมัติ' :
                       docData.status === 'rejected' ? 'ปฏิเสธ' : 'รอตรวจสอบ'}
                    </span>
                  </div>
                  
                  {docData.imageUrl && (
                    <div className="mb-2">
                      {docData.imageUrl.endsWith('.pdf') ? (
                        <a
                          href={docData.imageUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          ดู PDF
                        </a>
                      ) : (
                        <img
                          src={docData.imageUrl}
                          alt={documentLabels[docType]}
                          className="max-w-md rounded-lg"
                        />
                      )}
                    </div>
                  )}
                  
                  {docData.status === 'pending' && (
                    <div className="flex space-x-2 mt-2">
                      <button
                        onClick={() => onApproveDocument(verification.id, docType)}
                        className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                      >
                        อนุมัติ
                      </button>
                      <button
                        onClick={() => onRejectDocument(verification.id, docType)}
                        className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                      >
                        ปฏิเสธ
                      </button>
                    </div>
                  )}
                  
                  {docData.reviewNotes && (
                    <p className="text-sm text-gray-600 mt-2">
                      <strong>หมายเหตุ:</strong> {docData.reviewNotes}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Scores */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-3">คะแนน</h3>
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <p><strong>คะแนนการตรวจสอบ:</strong> {verification.verificationScore || 0}/175</p>
              <p><strong>Risk Score:</strong> {verification.riskScore || 100}/100</p>
            </div>
          </div>

          {/* Review Notes */}
          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-2">
              หมายเหตุ (สำหรับการอนุมัติ/ปฏิเสธ)
            </label>
            <textarea
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="3"
              placeholder="กรอกหมายเหตุ..."
            />
          </div>

          {/* Final Actions */}
          {verification.status !== 'approved' && verification.status !== 'rejected' && (
            <div className="flex space-x-2">
              <button
                onClick={() => onApproveVerification(verification.id)}
                className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
              >
                อนุมัติการตรวจสอบ
              </button>
              <button
                onClick={() => onRejectVerification(verification.id)}
                className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                ปฏิเสธการตรวจสอบ
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StoreVerificationPanel;






